import type { DropzoneInputProps, DropzoneRootProps } from "react-dropzone";
import type { Asset, AssetType } from "schemas/generated/asset";

import * as React from "react";

import { skipToken } from "@tanstack/react-query";
import { V1AssetsGrid } from "@v1/components/assets/AssetsGrid";
import { EmptyState } from "@v1/components/assets/EmptyState";
import { NoSearchResults } from "@v2/features/assets/components/asset-grid/NoSearchResults";
import {
  IMAGE_ACCEPT_FILE_EXTENSIONS,
  VIDEO_ACCEPT_FILE_EXTENSIONS,
} from "@v2/features/assets/constants";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { assetRouter } from "@v2/trpc/routes/asset";

type ReploAssetsGridProps = {
  assetTypes?: AssetType[];
  searchTerm: string;
  onAssetSelected?: (asset: Asset) => void;
  shouldAllowActions?: boolean;
  getDropzoneRootProps?: (
    props?: DropzoneRootProps | undefined,
  ) => DropzoneRootProps;
  getDropzoneInputProps?: (
    props?: DropzoneInputProps | undefined,
  ) => DropzoneInputProps;
  droppedFilesMap: Map<string, File>;
  isDragActive?: boolean;
  className?: string;
};

const INITIAL_ASSETS_TO_LOAD = 16;

export const V1ReploAssetsGrid: React.FC<ReploAssetsGridProps> = ({
  getDropzoneInputProps,
  getDropzoneRootProps,
  assetTypes = ["image", "video"],
  searchTerm,
  onAssetSelected,
  shouldAllowActions,
  droppedFilesMap,
  isDragActive = false,
  className,
}) => {
  const projectId = useCurrentProjectId();

  const { data, isLoading, fetchNextPage, hasNextPage } =
    assetRouter.list.useInfiniteQuery(
      projectId
        ? {
            projectId,
            types: assetTypes,
            limit: INITIAL_ASSETS_TO_LOAD,
            search: searchTerm,
          }
        : skipToken,
      {
        getNextPageParam: ({ nextCursor }) => nextCursor,
      },
    );

  const assets = React.useMemo(
    () => data?.pages?.flatMap((page) => page.assets) ?? [],
    [data],
  );

  // NOTE (Gabe 2025-02-26): We use this internal map so that assets loaded from the server are
  // removed. Otherwise they will reappear as loading if they are deleted after being uploaded.
  const [internalDroppedFilesMap, setInternalDroppedFilesMap] = React.useState<
    Map<string, File>
  >(new Map());
  React.useEffect(() => {
    const newDroppedFilesMap = new Map(droppedFilesMap);
    setInternalDroppedFilesMap(newDroppedFilesMap);
  }, [droppedFilesMap]);
  React.useEffect(() => {
    setInternalDroppedFilesMap((val) => {
      const newMap = new Map(val);
      assets.forEach((asset) => {
        newMap.delete(asset.id);
      });
      return newMap;
    });
  }, [assets]);

  // NOTE (Kevin, 2025-12-15): This creates temporary Asset objects for files that are currently
  // uploading, so they can be displayed in the grid with a loading state. Unlike Elements, Assets
  // don't have a folderId field (folders are only for pages).
  const uploadingAssets = React.useMemo(() => {
    return Array.from(internalDroppedFilesMap.entries())
      .map(([id, file]) => ({
        id,
        name: file.name,
        url: URL.createObjectURL(file),
        type: file.type.split("/")[0] as AssetType,
        fileExtension: file.name.split(".").pop() ?? "",
        createdAt: new Date(),
        updatedAt: new Date(),
        sizeBytes: file.size,
        width: null,
        height: null,
        projectId: projectId ?? "",
        altText: null,
        embeddingText: null,
      }))
      .filter(({ id }) => !assets.some((a) => a.id === id));
  }, [internalDroppedFilesMap, assets, projectId]);

  const dropDisabled = Boolean(searchTerm);

  const dropZoneRootProps = React.useMemo(() => {
    if (dropDisabled) {
      return undefined;
    }
    return getDropzoneRootProps?.({
      onClick: (event) => {
        if (assets.length > 0 || (uploadingAssets?.length ?? 0) > 0) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
    });
  }, [getDropzoneRootProps, dropDisabled, assets, uploadingAssets]);

  const dropZoneInputProps = React.useMemo(() => {
    if (dropDisabled) {
      return undefined;
    }
    return getDropzoneInputProps?.();
  }, [getDropzoneInputProps, dropDisabled]);

  return (
    <div {...dropZoneRootProps} className="contents">
      {dropZoneInputProps && <input {...dropZoneInputProps} />}
      <V1AssetsGrid.Root
        shouldAllowActions={shouldAllowActions}
        assets={assets}
        droppedAssets={uploadingAssets}
        isLoading={isLoading}
        isDragActive={isDragActive}
        onAssetSelected={onAssetSelected}
        hasNextPage={hasNextPage}
        fetchNextPage={() => void fetchNextPage()}
        searchTerm={searchTerm}
        emptyState={
          searchTerm ? (
            <NoSearchResults searchTerm={searchTerm} />
          ) : (
            <ReploAssetEmptyState assetTypes={assetTypes} />
          )
        }
        className={className}
      >
        <V1AssetsGrid.ListDetailed />
      </V1AssetsGrid.Root>
    </div>
  );
};

const ReploAssetEmptyState: React.FC<{
  assetTypes: AssetType[];
}> = ({ assetTypes }) => {
  const fileExtensions = [
    ...(assetTypes.includes("image") ? IMAGE_ACCEPT_FILE_EXTENSIONS : []),
    ...(assetTypes.includes("video") ? VIDEO_ACCEPT_FILE_EXTENSIONS : []),
  ];
  return (
    <EmptyState>
      <div className="text-xs font-semibold">Click to upload</div>
      <div className="text-xs">
        {" "}
        or drag and drop{" "}
        {fileExtensions
          .map((val) => val.replace(".", "").toUpperCase())
          .join(", ")}
      </div>
    </EmptyState>
  );
};
